import { useEffect, useMemo } from 'react';

import { EmptyState, Spinner } from '../components';
import { contentKind, normalizeContentType, stripLeadingOrdinal, type ContentKind } from '../config/content-types';
import { featureEnabled } from '../config/features';
import { CloseIcon, LayersIcon, PlayCircleIcon } from '../layout/icons';
import { useQltIztContentStore, useQltTimetableStore } from '../stores/qlt';
import { resolvePeriodCourseScope } from './periodLabels';
import type { BoardContextValue } from './useBoardContext';
import { useSelectUnit } from './useSelectUnit';

interface MaterialItem {
  id: string;
  name: string;
  type: string;
  courseId: string;
  kind: ContentKind;
  openable: boolean;
}

const KIND_LABELS: Record<ContentKind, string> = {
  slide: 'Bài giảng slide',
  video: 'Bài giảng video',
  assessment: 'Bài kiểm tra',
  other: 'Học liệu khác',
};

// a unit with no asset opens on an honest empty state, so the one dead end held back here is a
// real PDF unit with the renderer switched off
function canOpen(type: string): boolean {
  return normalizeContentType(type) !== 'pdf' || featureEnabled('board.pdf');
}

// the frame reads "Toán học 10"; the grade comes off the class title, which is all the board carries
function metaLabel(context: BoardContextValue): string {
  const subject = context.subjectTitle || 'Môn học';
  const title = context.classTitle || '';
  const grade = /^\d+/.exec(title);
  return `${subject} ${grade ? grade[0] : title}`.trim();
}

export interface BoardLectureSuggestionsProps {
  context: BoardContextValue;
  onClose: () => void;
}

export function BoardLectureSuggestions({ context, onClose }: BoardLectureSuggestionsProps) {
  const tree = useQltIztContentStore((state) => state.tree);
  const loading = useQltIztContentStore((state) => state.loading);
  const error = useQltIztContentStore((state) => state.error);
  const loadTree = useQltIztContentStore((state) => state.loadTree);
  const selectUnit = useSelectUnit();
  const meta = metaLabel(context);
  const cells = useQltTimetableStore((state) => state.todayCells);
  // a board opened by URL has no route state, so the period's timetable cell names grade and subject
  const scope = resolvePeriodCourseScope({ gradeId: context.gradeId, subjectId: context.subjectId }, cells, context);

  useEffect(() => {
    if (!context.classId) return;
    void loadTree({ class: context.classId, grade: scope.gradeId, subject: scope.subjectId });
  }, [context.classId, scope.gradeId, scope.subjectId, loadTree]);

  const items = useMemo(() => {
    const list: MaterialItem[] = [];
    tree.forEach((course) => course.sections.forEach((section) => section.units.forEach((unit) => {
      const type = unit.type || '';
      list.push({ id: unit.id, name: stripLeadingOrdinal(unit.name), type, courseId: course.id, kind: contentKind(type), openable: canOpen(type) });
    })));
    return list;
  }, [tree]);

  function retry() {
    void loadTree({ class: context.classId, grade: scope.gradeId, subject: scope.subjectId });
  }

  return (
    <section className="material-tray" aria-label="Học liệu liên quan">
      <div className="material-tray__head">
        <div>
          <h2 className="material-tray__title">Học liệu liên quan</h2>
          <p className="material-tray__subtitle">Nội dung dựa theo khung phân phối kế hoạch giảng dạy</p>
        </div>
        <button type="button" className="material-tray__close" aria-label="Đóng học liệu liên quan" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      {loading && items.length === 0 ? <Spinner label="Đang tải học liệu" size="small" /> : null}
      {error ? <div className="material-tray__error" role="alert"><strong>Không tải được học liệu</strong><p>{error}</p><button type="button" className="secondary-button" onClick={retry}>Thử lại</button></div> : null}
      {!loading && !error && items.length === 0 ? <EmptyState icon={<LayersIcon size={26} />} title="Chưa có học liệu liên quan" description="Khung phân phối của lớp này chưa có nội dung." compact={true} /> : null}

      {items.length > 0 ? (
        <div className="material-grid">
          {items.map((item) => (
            <div className="material-grid__cell" key={item.id}>
              <button
                type="button"
                className="material-card"
                disabled={!item.openable}
                title={item.name}
                onClick={() => selectUnit({ unitId: item.id, unitName: item.name, courseId: item.courseId })}
              >
                <span className="material-card__thumb">{item.kind === 'video' ? <span className="material-card__play"><PlayCircleIcon size={28} /></span> : null}</span>
                <strong className="material-card__name">{item.name}</strong>
                <span className="material-card__meta">
                  <span className="material-card__sub">{meta}</span>
                  <span className={`material-badge material-badge--${item.kind}`}>{KIND_LABELS[item.kind]}</span>
                </span>
                {item.openable ? null : <span className="material-card__blocked">Chưa mở được trên bảng</span>}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
